// client/src/App.js

import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Navigate } from 'react-router-dom';

// ── Eagerly loaded (used on every page) ──────────────────
import Navbar           from './components/Navbar';
import Footer           from './components/Footer';
import ProtectedRoute   from './components/ProtectedRoute';
import WhatsAppButton   from './components/WhatsAppButton';
import GoogleSetupModal from './components/GoogleSetupModal';
import ScrollToTop      from './hooks/ScrollToTop';
import Loader           from './components/Loader';

import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider }          from './context/CartContext';   // ← added
import { HeroProvider }          from './context/HeroContext';
import ChunkErrorBoundary        from './components/ChunkErrorBoundary';

import './App.css';

// ── Lazily loaded pages (code-split per route) ───────────
const Home               = lazy(() => import('./pages/Home'));
const About              = lazy(() => import('./pages/About'));
const Trips              = lazy(() => import('./pages/OurTrips'));
const TripDetails        = lazy(() => import('./pages/TripDetails'));
const Booking            = lazy(() => import('./pages/Booking'));
const Checkout           = lazy(() => import('./pages/Checkout'));
const Contact            = lazy(() => import('./pages/Contact'));
const Login              = lazy(() => import('./pages/Login'));
const Register           = lazy(() => import('./pages/Register'));
const BookingsTab = lazy(() => import('./pages/Bookings/BookingsTab'));

const NotFound           = lazy(() => import('./pages/NotFound'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const FAQ                = lazy(() => import('./pages/FAQ'));
const HealthSafety       = lazy(() => import('./pages/HealthSafety'));
const PrivacyPolicy      = lazy(() => import('./pages/PrivacyPolicy'));

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

/* ─────────────────────────────────────────────────────────
   GoogleSetupGuard
───────────────────────────────────────────────────────── */
function GoogleSetupGuard({ children }) {
  const { needsSetup } = useAuth();
  const { pathname }   = useLocation();

  const isAuthPage = pathname === '/login' || pathname === '/register';

  return (
    <>
      {children}
      {needsSetup && !isAuthPage && <GoogleSetupModal />}
    </>
  );
}

/* ─── Auth layout ────────────────────────────────────── */
const AuthLayout = ({ children }) => <>{children}</>;

/* ─── Main layout ────────────────────────────────────── */
const MainLayout = ({ children }) => {
  const location = useLocation();
  const { pathname } = location;

  const isTripDetailsPage     = /^\/trips\/[^/]+$/.test(pathname);
  const isTripsPage           = pathname === '/trips' || pathname === '/trips/';
  const isBookingPage         = /^\/booking(\/|$)/.test(pathname);
  const isCheckoutPage        = pathname === '/checkout' || pathname === '/checkout/';
  const isCheckoutSuccessPage = isCheckoutPage && !!location.state?.checkoutSuccess;

  const isProfilePage = ['/mybookings', '/mywishlist', '/wallet', '/myprofile'].includes(pathname) || pathname.startsWith('/mybookings/');

  const hideFooter   = isTripsPage || isTripDetailsPage || isBookingPage || isCheckoutPage || isProfilePage;
 const hideWhatsApp = isTripDetailsPage || isBookingPage || isCheckoutPage || isProfilePage;
  const hideNavbar   = isProfilePage || isCheckoutSuccessPage;

return (
  <>
    {!hideNavbar && <Navbar />}
      <main className="page-content">
        {children}
      </main>
      {!hideFooter   && <Footer />}
      {!hideWhatsApp && <WhatsAppButton />}
    </>
  );
};

/* ─── All routes ─────────────────────────────────────── */
function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <HeroProvider>
        <GoogleSetupGuard>
          <ChunkErrorBoundary>
          <Suspense fallback={<Loader />}>
            <Routes>
              {/* ── Auth pages ── */}
              <Route path="/login"    element={<AuthLayout><Login    /></AuthLayout>} />
              <Route path="/register" element={<AuthLayout><Register /></AuthLayout>} />

              {/* ── Public ── */}
              <Route path="/"          element={<MainLayout><Home                 /></MainLayout>} />
              <Route path="/about"     element={<MainLayout><About                /></MainLayout>} />
              <Route path="/trips"     element={<MainLayout><Trips                /></MainLayout>} />
              <Route path="/trips/:id" element={<MainLayout><TripDetails          /></MainLayout>} />
              <Route path="/contact"   element={<MainLayout><Contact              /></MainLayout>} />
              <Route path="/terms"     element={<MainLayout><TermsAndConditions   /></MainLayout>} />
              <Route path="/faq"       element={<MainLayout><FAQ                  /></MainLayout>} />
              <Route path="/safety"    element={<MainLayout><HealthSafety         /></MainLayout>} />
              <Route path="/privacy"   element={<MainLayout><PrivacyPolicy        /></MainLayout>} />

              {/* ── /booking → redirect to mybookings ── */}
              <Route path="/booking" element={<Navigate to="/mybookings" replace />} />

              {/* ── Protected ── */}
              <Route
                path="/booking/:id"
                element={<MainLayout><ProtectedRoute><Booking  /></ProtectedRoute></MainLayout>}
              />
              <Route
                path="/checkout"
                element={<MainLayout><ProtectedRoute><Checkout /></ProtectedRoute></MainLayout>}
              />
              <Route path="/mybookings" element={<MainLayout><ProtectedRoute><BookingsTab /></ProtectedRoute></MainLayout>} />
              <Route path="/mybookings/:bookingId" element={<MainLayout><ProtectedRoute><BookingsTab /></ProtectedRoute></MainLayout>} />
              <Route path="/mybookings/:bookingId/review" element={<MainLayout><ProtectedRoute><BookingsTab /></ProtectedRoute></MainLayout>} />
              <Route path="/mywishlist" element={<MainLayout><ProtectedRoute><BookingsTab /></ProtectedRoute></MainLayout>} />
              <Route path="/wallet"     element={<MainLayout><ProtectedRoute><BookingsTab /></ProtectedRoute></MainLayout>} />
              <Route path="/myprofile"  element={<MainLayout><ProtectedRoute><BookingsTab /></ProtectedRoute></MainLayout>} />

              {/* ── 404 ── */}
              <Route path="*" element={<MainLayout><NotFound /></MainLayout>} />
            </Routes>
          </Suspense>
          </ChunkErrorBoundary>
        </GoogleSetupGuard>
      </HeroProvider>
    </>
  );
}

/* ─── Root ───────────────────────────────────────────── */
export default function App() {
  useEffect(() => {
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    document.body.appendChild(outer);
    const inner = document.createElement('div');
    outer.appendChild(inner);
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    document.body.removeChild(outer);
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
  }, []);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <CartProvider>          {/* ← added: must be inside AuthProvider so useAuth() works */}
          <Router
            future={{
              v7_startTransition:   true,
              v7_relativeSplatPath: true,
            }}
          >
            <AppRoutes />
          </Router>
        </CartProvider>         {/* ← added */}
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}
